import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtUSD, fmtPct, fmtNum, gainClass, relTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";

export default function Profile() {
  const { id } = useParams();
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [social, setSocial] = useState({ followers: 0, following: 0, is_following: false });
  const [copying, setCopying] = useState({ is_copying: false, enabled: false });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "", avatar_url: "" });

  const load = () => {
    api.get(`/users/${id}`).then(({ data }) => {
      setData(data);
      setForm({ name: data.name || "", bio: data.bio || "", avatar_url: data.avatar_url || "" });
    }).catch(() => setData(null));
    api.get(`/users/${id}/social`).then(({ data }) => setSocial(data)).catch(() => {});
    api.get(`/copy/is-copying/${id}`).then(({ data }) => setCopying(data)).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isMe = user && user.id === id;

  const toggleFollow = async () => {
    try {
      if (social.is_following) {
        await api.delete(`/users/${id}/follow`);
        toast.success("Unfollowed");
      } else {
        await api.post(`/users/${id}/follow`);
        toast.success("Following");
      }
      load();
    } catch {
      toast.error("Could not update follow");
    }
  };

  const toggleCopy = async () => {
    try {
      if (copying.is_copying) {
        await api.delete(`/copy/follow/${id}`);
        toast.success("Stopped copying");
      } else {
        await api.post(`/copy/follow/${id}`);
        toast.success("Copying trades from this trader");
      }
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not toggle copy";
      toast.error(typeof msg === "string" ? msg : "Failed");
    }
  };

  const save = async () => {
    try {
      const { data: fresh } = await api.patch("/users/me", form);
      setUser(fresh);
      toast.success("Profile updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error("Could not save");
    }
  };

  if (!data) return <p className="text-sm text-gray-500">Loading trader…</p>;

  return (
    <div className="space-y-8" data-testid="profile-root">
      <Card className="border border-gray-200 rounded-2xl p-8">
        <div className="flex items-start gap-6 flex-wrap">
          <Avatar className="h-24 w-24 border border-gray-200">
            <AvatarImage src={data.avatar_url} alt={data.name} />
            <AvatarFallback className="text-2xl">{data.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-[240px]">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Trader</p>
            <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-1 flex items-center gap-2" data-testid="profile-name">
              {data.name}
              {data.verified_pro && (
                <BadgeCheck
                  size={22}
                  className="text-gain"
                  data-testid="verified-badge"
                  title={`Verified Pro until ${data.verified_pro_expires_at ? new Date(data.verified_pro_expires_at).toLocaleDateString() : ""}`}
                />
              )}
            </h1>
            <p className="text-gray-500 mt-2 text-sm">{data.bio || "No bio yet."}</p>
            <p className="text-xs text-gray-400 mt-3">Joined {new Date(data.created_at).toLocaleDateString()}</p>
            <div className="flex items-center gap-4 mt-3 text-sm">
              <span data-testid="social-followers">
                <span className="font-semibold">{social.followers}</span>{" "}
                <span className="text-gray-500">followers</span>
              </span>
              <span data-testid="social-following">
                <span className="font-semibold">{social.following}</span>{" "}
                <span className="text-gray-500">following</span>
              </span>
            </div>
          </div>
          {isMe ? (
            <Button
              data-testid="edit-profile-btn"
              variant="outline"
              onClick={() => setEditing((s) => !s)}
              className="rounded-full border-gray-200"
            >
              {editing ? "Cancel" : "Edit profile"}
            </Button>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                data-testid="follow-btn"
                onClick={toggleFollow}
                className={`rounded-full ${
                  social.is_following
                    ? "bg-gray-100 text-black hover:bg-gray-200"
                    : "bg-brand text-white hover:bg-brand-deep"
                }`}
              >
                {social.is_following ? "Following" : "Follow"}
              </Button>
              <Button
                data-testid="copy-btn"
                onClick={toggleCopy}
                variant="outline"
                className={`rounded-full border-gray-200 ${
                  copying.is_copying ? "bg-gain-soft text-gain border-transparent" : ""
                }`}
              >
                {copying.is_copying ? "Copying ✓" : "Copy trader"}
              </Button>
            </div>
          )}
        </div>

        {isMe && editing && (
          <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="profile-edit-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Name</Label>
              <Input
                data-testid="edit-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Avatar URL</Label>
              <Input
                data-testid="edit-avatar"
                value={form.avatar_url}
                onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Bio</Label>
              <Textarea
                data-testid="edit-bio"
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 min-h-[80px]"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                data-testid="save-profile-btn"
                onClick={save}
                className="rounded-full bg-brand text-white hover:bg-brand-deep"
              >
                Save profile
              </Button>
            </div>
          </div>
        )}
      </Card>

      <div className="grid md:grid-cols-4 gap-4">
        <StatCard label="Portfolio value" value={fmtUSD(data.portfolio_value)} testId="stat-portfolio" />
        <StatCard
          label="Total PnL"
          value={fmtUSD(data.total_pnl)}
          sub={fmtPct(data.total_pnl_pct)}
          cls={gainClass(data.total_pnl)}
          testId="stat-pnl"
        />
        <StatCard label="Win rate" value={`${data.win_rate.toFixed(0)}%`} testId="stat-winrate" />
        <StatCard label="Total trades" value={String(data.trades_count)} testId="stat-tradecount" />
      </div>

      <Card className="border border-gray-200 rounded-2xl p-6" data-testid="profile-trades">
        <p className="font-display font-semibold text-lg mb-4">Recent trades</p>
        {data.recent_trades.length === 0 ? (
          <p className="text-sm text-gray-500">No trades yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.recent_trades.map((t) => (
              <div key={t._id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium">
                    <span className={t.side === "buy" ? "text-gain" : "text-loss"}>{t.side.toUpperCase()}</span>{" "}
                    {fmtNum(t.quantity)} {t.symbol}
                  </p>
                  <p className="text-xs text-gray-500">{relTime(t.created_at)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-sm font-mono">{fmtUSD(t.total)}</p>
                  {t.grade && <Badge variant="secondary" className="text-[10px]">{t.grade}</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function StatCard({ label, value, sub, cls = "", testId }) {
  return (
    <Card className="border border-gray-200 rounded-2xl p-6" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</p>
      <p className={`font-display text-2xl font-medium tabular-nums mt-2 ${cls}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${cls}`}>{sub}</p>}
    </Card>
  );
}
